/**
 * S-75: 管理者 MFA（TOTP）の純関数ヘルパー。
 *
 * firebase/auth に依存しない pure ロジックのみを集約し、単体テストで回帰ネットを張る。
 * SDK 呼び出しを伴う処理は `mfa.ts`、React UI は `MfaChallenge.tsx` /
 * `MfaSettings.tsx` に分離する。
 *
 * 本パッケージ（@kawai/auth-bootstrap）は Web SDK / React 依存を持てる（apps からのみ
 * 参照される）。`@kawai/shared` は Functions からも参照されるため MFA コードは置かない。
 */
/**
 * 認証アプリが出す TOTP コードは 6 桁の数字。空白（コピペ時の前後空白・区切り）を
 * 除去して正規化する。
 */
export function normalizeTotpCode(raw) {
    return raw.replace(/\s+/g, '');
}
/**
 * TOTP 確認コードのフォーマット検証。
 * - 妥当: `null`
 * - 不正: 表示用の日本語エラーメッセージ
 *
 * 空白除去後に「6 桁の数字」のみ許容する。
 */
export function validateTotpCode(raw) {
    const code = normalizeTotpCode(raw);
    if (!code)
        return '確認コードを入力してください';
    if (!/^\d{6}$/.test(code))
        return '確認コードは 6 桁の数字です';
    return null;
}
/**
 * unknown な error から Firebase の `error.code`（`auth/...`）を安全に取り出す。
 * 取り出せない場合は `undefined`。
 */
export function getAuthErrorCode(err) {
    if (err && typeof err === 'object' && 'code' in err) {
        const code = err.code;
        if (typeof code === 'string')
            return code;
    }
    return undefined;
}
/**
 * サインイン時に「第二要素が必要」を示す Firebase エラーかどうか。
 * `signInWithEmailAndPassword` は MFA 登録済ユーザーに対して
 * `auth/multi-factor-auth-required` を throw する。
 */
export function isMfaRequiredError(err) {
    return getAuthErrorCode(err) === 'auth/multi-factor-auth-required';
}
/**
 * MFA（enrollment / challenge）系の Firebase AuthError → 表示用日本語メッセージ。
 * 既存の `mapAuthErrorMessage`（各 app の auth.tsx）を MFA 文脈に特化した版。
 */
export function mapMfaErrorMessage(code) {
    switch (code) {
        case 'auth/invalid-verification-code':
        case 'auth/invalid-verification-id':
        case 'auth/code-expired':
            return '確認コードが正しくありません。認証アプリに表示された最新のコードを入力してください';
        case 'auth/totp-challenge-timeout':
            return '確認コードの有効期限が切れました。サインインからやり直してください';
        case 'auth/missing-multi-factor-session':
        case 'auth/multi-factor-info-not-found':
            return 'MFA セッションが無効です。サインインからやり直してください';
        case 'auth/maximum-second-factor-count-exceeded':
            return '登録できる認証手段の上限に達しています';
        case 'auth/second-factor-already-in-use':
            return 'この認証手段は既に登録されています';
        case 'auth/requires-recent-login':
            return 'セキュリティのため再サインインが必要です。一度サインアウトしてやり直してください';
        case 'auth/unverified-email':
        case 'auth/email-not-verified':
            return 'メールアドレスの確認が完了していないため 2 段階認証を登録できません。管理者にメールアドレスの確認を依頼してください';
        case 'auth/too-many-requests':
            return '試行回数が多すぎます。しばらく待ってから再度お試しください';
        case 'auth/network-request-failed':
            return 'ネットワークエラーが発生しました。接続を確認してください';
        case 'auth/operation-not-allowed':
        case 'auth/unsupported-first-factor':
        case 'auth/admin-restricted-operation':
            return 'MFA が有効化されていません。管理者にお問い合わせください';
        default:
            return '確認に失敗しました。時間をおいて再度お試しください';
    }
}
/**
 * TOTP の enrollment 表示名。Firebase の enroll(assertion, displayName) に渡す。
 * 認証アプリ名は任意のため固定文言 + 登録日（JST）にする。
 *
 * @param nowJstDate `YYYY-MM-DD`（JST）。呼出側で `dateJst` 等から渡す。
 */
export function buildTotpDisplayName(nowJstDate) {
    return `認証アプリ (${nowJstDate} 登録)`;
}
//# sourceMappingURL=mfaPure.js.map